import { useState } from 'react'
import { motion } from 'framer-motion'
import { Celebration } from '@/components/Celebration'
import { Kicker } from '@/components/Kicker'
import { SIGNATURES, signatureForPerson, setSignatureForPerson, type SignatureKind } from '@/lib/celebrationSignatures'
import { haptic } from '@/lib/haptics'

/**
 * CelebrationSignaturePicker — one row per person, shown in PersonProfile.
 * Tap a signature chip → previews the celebration overlay live so the user
 * can pick the one that fits the person. Persisted per person_id in localStorage.
 */
export function CelebrationSignaturePicker({ personId, personName }: { personId: string; personName: string }) {
  const [chosen, setChosen] = useState<SignatureKind>(signatureForPerson(personId).slug)
  const [preview, setPreview] = useState<SignatureKind | null>(null)

  function pick(slug: SignatureKind) {
    setChosen(slug)
    setSignatureForPerson(personId, slug)
    setPreview(slug)
    haptic('success')
  }

  return (
    <>
      <section>
        <Kicker>Celebration signature</Kicker>
        <div className="mt-2 text-tag text-text-tertiary">
          Fires when you log a milestone for {personName}. Tap to preview.
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          {SIGNATURES.map((s) => (
            <motion.button
              key={s.slug}
              type="button"
              onClick={() => pick(s.slug)}
              whileTap={{ scale: 0.96 }}
              className={`glass-tile flex flex-col items-center justify-center py-4 border-2 transition-all ${
                chosen === s.slug ? 'border-accent-coral' : 'border-transparent'
              }`}
              style={{ borderRadius: '18px' }}
              aria-pressed={chosen === s.slug}
            >
              <div className="text-title" style={{ fontSize: 32 }}>{s.glyph}</div>
              <div className="text-caption font-medium mt-1">{s.label}</div>
              <div className="text-tag text-text-tertiary text-center px-1 leading-tight mt-0.5">{s.description}</div>
            </motion.button>
          ))}
        </div>
      </section>

      <Celebration
        open={preview !== null}
        title={`${personName}!`}
        subtitle="Preview"
        palette="early_care"
        signature={preview ?? undefined}
        onDismiss={() => setPreview(null)}
      />
    </>
  )
}
